import { Component, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import {
  IdTagCreateOrEditModalComponent,
  IdTagCreateOrEditModalComponentResult,
} from '../id-tag-create-or-edit-modal/id-tag-create-or-edit-modal.component';
import { IdTag } from '../id-tag-model';
import { PageEvent } from '@angular/material/paginator';
import { Department, DepartmentSearchEvent } from '../../department/department.model';
import { EnvironmentLoaderService } from '../../../shared/services/environment-loader.service';
import { Organization } from '../../organization/organization-models';
import { AppState } from 'src/app/app.state';
import { ChargingUserService } from '../../../shared/services/charging-user.service';
import { CustomCombinedIdTagTypeEnum } from '../../../shared/enums/custom-combined-id-tag-type.enum';
import { RequiresFeatureRoleDirective } from '../../../shared/directives/requires-feature-role.directive';
import { IdTagsListComponent } from '../id-tags-list/id-tags-list.component';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { IdTagListStateService } from './id-tag-list.state.service';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { TranslateIdTagStatusEnumPipe } from 'src/app/shared/pipes/translate-id-tag-status-enum.pipe';
import { TranslateIdTagCombinedTypeEnumPipe } from 'src/app/shared/pipes/translate-id-tag-combined-type-enum.pipe';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';
import { IdTagProcessModalComponent } from '../id-tag-process-modal/id-tag-process-modal.component';

@Component({
  selector: 'adm-id-tags-overview',
  templateUrl: './id-tags-overview.component.html',
  styleUrls: ['./id-tags-overview.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    TextSearchComponent,
    IdTagsListComponent,
    AdmOrgTypeaheadComponent,
    AdmDepTypeaheadComponent,
    AdminEnumSelectListComponent,
    MatSlideToggle,
    MatButton,
  ],
  providers: [IdTagListStateService, TranslateIdTagStatusEnumPipe, TranslateIdTagCombinedTypeEnumPipe],
})
export class IdTagsOverviewComponent {
  private dialog = inject(MatDialog);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  state = inject(IdTagListStateService);
  appState = inject(AppState);
  chargingUserService = inject(ChargingUserService);
  public roles = this.environmentLoaderService.virtualRoles;
  departments = signal<Department[]>([]);
  focusedUser = toSignal(this.state.focusedUser$, { initialValue: null });
  readonly translateIdTagStatusPipe = inject(TranslateIdTagStatusEnumPipe);
  readonly translateIdTagCombinedTypePipe = inject(TranslateIdTagCombinedTypeEnumPipe);

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.ToFirstPage({
        query: value && value.length > 0 ? value : null,
      });
    });
  };

  updateIncludeInactive(checked: boolean) {
    this.state.ToFirstPage({ includeInactive: checked, idTagStatus: checked ? this.state.form.value.idTagStatus : null });
  }

  public customCombinedIdTagTypeEnum: CustomCombinedIdTagTypeEnum[] = Object.values(
    CustomCombinedIdTagTypeEnum
  ) as CustomCombinedIdTagTypeEnum[];

  public pageEvent: PageEvent = new PageEvent();
  public departmentSearchEvent: DepartmentSearchEvent = new DepartmentSearchEvent();
  public organizations: Organization[];
  public filteredOrganizations: Organization[];
  public filteredDepartments$: Observable<Department[]>;

  updateIdTagStatus(value: IdTagStatusEnum | null | undefined) {
    const nextValue = value ?? null;
    if (!this.state.form.value.includeInactive && (nextValue === IdTagStatusEnum.Inactive || nextValue === IdTagStatusEnum.NoIdTagGroup)) {
      this.state.ToFirstPage({ idTagStatus: null });
      return;
    }
    this.state.ToFirstPage({ idTagStatus: nextValue });
  }

  organizationSelected = (org: Organization | null | undefined) => {
    this.state.ToFirstPage({
      organizationId: org ? org.id : null,
    });
  };

  clearOrganizations = () => {
    this.state.resetDepartmentFilterSignal.set({ reset: true });
    this.state.ToFirstPage({
      departmentId: null,
    });
  };

  updatecustomCombinedIdTagType(val: CustomCombinedIdTagTypeEnum | null | undefined): void {
    this.state.ToFirstPage({
      customCombinedIdTagType: val ?? null,
    });
  }

  departmentFetched = (departments: Department[] | null) => {
    this.departments.set(departments ? departments : []);
  };

  departmentSelected = (department: Department | null | undefined) => {
    this.state.ToFirstPage({
      departmentId: department ? department.id : null,
    });
  };

  public openCreateIdTagModal() {
    const dialogRef = this.dialog.open(IdTagCreateOrEditModalComponent, {
      data: {
        departments: this.departments(),
        stateService: this.state,
      },
    });
    dialogRef.afterClosed().subscribe((result: IdTagCreateOrEditModalComponentResult) => {
      if (result.action !== 'close') {
        if (result.action === 'process' && result.idTag) {
          this.processIdTag(result.idTag);
        } else {
          if (result.idTag) {
            this.refreshAfterUpdate(result.idTag);
          }
        }
      }
    });
  }

  processIdTag(idTag: IdTag) {
    const dialogRef = this.dialog.open(IdTagProcessModalComponent, {
      data: {
        idTagId: idTag.id,
      },
    });

    dialogRef.afterClosed().subscribe((idTag: IdTag) => {
      this.refreshAfterUpdate(idTag);
    });
  }

  refreshAfterUpdate(idTag: IdTag) {
    this.state.triggerRefresh();
  }

  public resetFilter() {
    this.state.resetFilter();
  }

  public resetDepartment() {
    this.state.form.patchValue({
      departmentId: null,
    });
    this.state.resetDepartmentFilterSignal.set({ reset: true });
  }
}
